import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Admin → Meta Intelligence (v3.84). Lifetime ad-history archive across ALL
 * users' connected Meta accounts: harvest every ad ever run (creatives
 * mirrored durably to CDN + lifetime KPIs), vision-analyze top spenders, and
 * browse/sort the archive — the raw material for back-end winning-ads
 * understanding.
 */

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    let text = "";
    try { text = await res.text(); } catch {}
    throw new Error(`${res.status}${text ? `: ${text.slice(0, 200)}` : ""}`);
  }
  return res.json() as Promise<T>;
}

interface IntelAccount {
  id: number;
  account_id: string;
  account_name: string;
  currency: string;
  is_active: boolean;
  lifetime_harvest_status: string | null;
  lifetime_harvest_at: string | null;
  lifetime_harvest_error: string | null;
  harvested_ads_count: number | null;
  user_id: number;
  user_email: string | null;
  fb_user_name: string | null;
  connection_status: string;
  archived_count: number;
  analyzed_count: number;
  total_spend: string;
}

interface ArchiveAd {
  id: number;
  ad_id: string;
  name: string | null;
  campaign_name: string | null;
  headline: string | null;
  body: string | null;
  call_to_action: string | null;
  cdn_image_url: string | null;
  meta_thumbnail_url: string | null;
  status: string | null;
  effective_status: string | null;
  first_active_date: string | null;
  last_active_date: string | null;
  impressions: number;
  clicks: number;
  spend: string;
  ctr: string | null;
  purchases: number;
  purchase_value: string;
  roas: string | null;
  cost_per_purchase: string | null;
  ad_style: string | null;
  hook_type: string | null;
  composition_family: string | null;
  analysis_notes: string | null;
  analyzed_at: string | null;
}

const SORTS = [
  { key: "spend", label: "Spend" },
  { key: "roas", label: "ROAS" },
  { key: "purchases", label: "Purchases" },
  { key: "purchase_value", label: "Revenue" },
  { key: "ctr", label: "CTR" },
  { key: "impressions", label: "Impressions" },
  { key: "last_active", label: "Last active" },
];

function money(v: string | number | null | undefined, currency = "USD"): string {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });
}

function AccountRow({ acct, selected, onSelect }: { acct: IntelAccount; selected: boolean; onSelect: () => void }) {
  const qc = useQueryClient();
  const harvest = useMutation({
    mutationFn: () => fetchJson(`/api/admin/meta-intel/accounts/${acct.id}/harvest`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meta-intel-accounts"] }),
  });
  const analyze = useMutation({
    mutationFn: () => fetchJson(`/api/admin/meta-intel/accounts/${acct.id}/analyze`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cap: 100 }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meta-intel-accounts"] }),
  });

  const running = acct.lifetime_harvest_status === "running";

  return (
    <tr
      className={`border-t border-[#E5E2DC] cursor-pointer ${selected ? "bg-blue-50" : "hover:bg-[#F5F3EE]"}`}
      onClick={onSelect}
      data-testid={`intel-account-${acct.id}`}
    >
      <td className="px-4 py-2 text-sm">
        <div className="font-medium text-neutral-800">{acct.account_name}</div>
        <div className="text-[11px] text-[#746F66]">{acct.account_id} · {acct.user_email ?? `user ${acct.user_id}`}</div>
      </td>
      <td className="px-4 py-2 text-sm">
        {running ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">harvesting…</span>
        ) : acct.lifetime_harvest_status === "error" ? (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800" title={acct.lifetime_harvest_error ?? ""}>error</span>
        ) : acct.lifetime_harvest_status === "completed" ? (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">harvested</span>
        ) : (
          <span className="text-[#746F66] text-xs">never</span>
        )}
      </td>
      <td className="px-4 py-2 text-right font-mono text-sm tabular-nums">{acct.archived_count.toLocaleString()}</td>
      <td className="px-4 py-2 text-right font-mono text-sm tabular-nums">{acct.analyzed_count.toLocaleString()}</td>
      <td className="px-4 py-2 text-right font-mono text-sm tabular-nums">{money(acct.total_spend, acct.currency)}</td>
      <td className="px-4 py-2">
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => harvest.mutate()}
            disabled={running || harvest.isPending}
            className="rounded border border-[#E5E2DC] bg-white px-2 py-1 text-xs hover:bg-[#F5F3EE] disabled:opacity-40"
            data-testid={`harvest-${acct.id}`}
          >
            {running ? "Running…" : acct.lifetime_harvest_status === "completed" ? "Re-harvest" : "Harvest lifetime"}
          </button>
          <button
            onClick={() => analyze.mutate()}
            disabled={acct.archived_count === 0 || analyze.isPending}
            className="rounded border border-[#E5E2DC] bg-white px-2 py-1 text-xs hover:bg-[#F5F3EE] disabled:opacity-40"
            title="Vision-analyze the top 100 un-analyzed ads by spend"
            data-testid={`analyze-${acct.id}`}
          >
            Analyze top 100
          </button>
        </div>
      </td>
    </tr>
  );
}

function AdCard({ ad, currency }: { ad: ArchiveAd; currency: string }) {
  const img = ad.cdn_image_url ?? ad.meta_thumbnail_url;
  return (
    <div className="rounded border border-[#E5E2DC] bg-white overflow-hidden flex flex-col" data-testid={`archive-ad-${ad.ad_id}`}>
      <div className="aspect-square bg-neutral-100 relative">
        {img ? (
          <img src={img} alt={ad.name ?? ad.ad_id} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-[#746F66]">
            {ad.body ? <span className="p-3 line-clamp-6 text-[#746F66]">{ad.body}</span> : "no creative"}
          </div>
        )}
        {ad.effective_status === "ACTIVE" && (
          <span className="absolute top-1.5 left-1.5 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white">LIVE</span>
        )}
        {ad.roas != null && Number(ad.roas) > 0 && (
          <span className={`absolute top-1.5 right-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold text-white ${Number(ad.roas) >= 2 ? "bg-emerald-600/90" : Number(ad.roas) >= 1 ? "bg-amber-500/90" : "bg-red-500/90"}`}>
            {Number(ad.roas).toFixed(1)}x
          </span>
        )}
      </div>
      <div className="p-2.5 space-y-1.5 flex-1 flex flex-col">
        <div className="text-xs font-medium text-neutral-800 line-clamp-1" title={ad.name ?? undefined}>{ad.name ?? ad.ad_id}</div>
        <div className="text-[11px] text-[#746F66] line-clamp-1">{ad.campaign_name}</div>
        <div className="grid grid-cols-3 gap-1 text-[11px] font-mono tabular-nums text-[#746F66]">
          <div title="Spend">{money(ad.spend, currency)}</div>
          <div title="Purchases">{ad.purchases}p</div>
          <div title="Revenue">{money(ad.purchase_value, currency)}</div>
        </div>
        {(ad.ad_style || ad.composition_family) && (
          <div className="flex flex-wrap gap-1 mt-auto pt-1">
            {ad.ad_style && <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px]">{ad.ad_style}</span>}
            {ad.hook_type && <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px]">{ad.hook_type}</span>}
            {ad.composition_family && <span className="rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[10px]">{ad.composition_family}</span>}
          </div>
        )}
        {ad.analysis_notes && (
          <div className="text-[11px] text-[#746F66] line-clamp-2" title={ad.analysis_notes}>{ad.analysis_notes}</div>
        )}
      </div>
    </div>
  );
}

export default function AdminMetaIntelPage() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sort, setSort] = useState("spend");
  const [q, setQ] = useState("");

  const accounts = useQuery({
    queryKey: ["meta-intel-accounts"],
    queryFn: () => fetchJson<{ accounts: IntelAccount[] }>("/api/admin/meta-intel/accounts"),
    // Keep harvest progress fresh while any account is running
    refetchInterval: (query) =>
      (query.state.data?.accounts ?? []).some((a) => a.lifetime_harvest_status === "running") ? 5000 : false,
  });

  const rows = accounts.data?.accounts ?? [];
  const selected = rows.find((a) => a.id === selectedId) ?? null;

  const ads = useQuery({
    queryKey: ["meta-intel-ads", selectedId, sort, q],
    queryFn: () => fetchJson<{ ads: ArchiveAd[] }>(`/api/admin/meta-intel/accounts/${selectedId}/ads?sort=${sort}&limit=120${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    enabled: selectedId != null,
  });

  return (
    <div className="mx-auto w-full max-w-[1440px]">
      <h1 className="mb-1 text-2xl font-semibold">Meta Intelligence</h1>
      <p className="mb-6 text-sm text-[#746F66]">
        Lifetime ad-history archive across every connected Meta account. Harvest pulls every ad ever run (creatives mirrored permanently, lifetime KPIs up to Meta's 37-month history cap); Analyze tags top spenders with style / hook / composition via vision.
      </p>

      <section className="rounded border border-[#E5E2DC] bg-white mb-8">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-[#746F66]">
              <tr>
                <th className="px-4 py-2 font-medium">Account</th>
                <th className="px-4 py-2 font-medium">Harvest</th>
                <th className="px-4 py-2 font-medium text-right">Ads archived</th>
                <th className="px-4 py-2 font-medium text-right">Analyzed</th>
                <th className="px-4 py-2 font-medium text-right">Lifetime spend</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <AccountRow key={a.id} acct={a} selected={a.id === selectedId} onSelect={() => setSelectedId(a.id)} />
              ))}
            </tbody>
          </table>
          {accounts.isLoading && <div className="p-6 text-center text-sm text-[#746F66]">Loading accounts…</div>}
          {!accounts.isLoading && rows.length === 0 && (
            <div className="p-6 text-center text-sm text-[#746F66]">No connected Meta accounts yet.</div>
          )}
        </div>
      </section>

      {selected && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold">{selected.account_name} — archive</h2>
            <div className="flex items-center gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name / headline / campaign"
                className="rounded border border-[#E5E2DC] px-3 py-1.5 text-sm w-64"
                data-testid="archive-search"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="rounded border border-[#E5E2DC] px-2 py-1.5 text-sm"
                data-testid="archive-sort"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
              </select>
            </div>
          </div>
          {ads.isLoading && <div className="p-6 text-center text-sm text-[#746F66]">Loading archive…</div>}
          {!ads.isLoading && (ads.data?.ads ?? []).length === 0 && (
            <div className="rounded border border-dashed border-[#E5E2DC] p-8 text-center text-sm text-[#746F66]">
              Nothing archived yet — hit "Harvest lifetime" above.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {(ads.data?.ads ?? []).map((ad) => <AdCard key={ad.id} ad={ad} currency={selected.currency} />)}
          </div>
        </section>
      )}
    </div>
  );
}
